import React from 'react';
import { Link } from 'react-router';
import {
  ChartBarSquareIcon,
  AcademicCapIcon,
  PresentationChartLineIcon,
  ArrowRightIcon,
  PhoneArrowDownLeftIcon,
  DocumentMagnifyingGlassIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '../../../components/layouts';
import { Seo } from '../../../seo/Seo';
import {
  DeckHero,
  DeckSection,
  DeckSectionHeader,
  DeckDisclaimer,
  DeckStepGrid,
  DeckFaqList,
  DeckFinalCta,
  useDeckAnimateOnScroll,
} from '../../../components/landing/deck';

const QualityLandingPage: React.FC = () => {
  const { t } = useTranslation(['landing', 'common']);
  useDeckAnimateOnScroll();

  const modules = [
    {
      key: 'qaScoring' as const,
      icon: ChartBarSquareIcon,
      benefits: ['effort', 'sentiment', 'empathy', 'resolution'] as const,
    },
    {
      key: 'coaching' as const,
      icon: AcademicCapIcon,
      benefits: ['tiers', 'priority', 'contextual', 'tracking'] as const,
    },
    {
      key: 'sales' as const,
      icon: PresentationChartLineIcon,
      benefits: ['objections', 'persuasion', 'talkTime', 'effectiveness'] as const,
    },
  ];

  const howItWorksSteps = [
    { key: 'ingestion', icon: PhoneArrowDownLeftIcon },
    { key: 'transcription', icon: DocumentMagnifyingGlassIcon },
    { key: 'evaluation', icon: ChartBarSquareIcon },
    { key: 'scoring', icon: BellAlertIcon },
  ] as const;

  const faqItems = (['scoring', 'coaching', 'tracking', 'compliance', 'speed'] as const).map(
    (k) => ({
      question: t(`landing:qualityPage.faq.items.${k}.question`),
      answer: t(`landing:qualityPage.faq.items.${k}.answer`),
    })
  );

  return (
    <PublicLayout transparentHeader>
      <Seo route="/quality" includeSoftwareApplication />

      <DeckHero
        title={t('landing:qualityPage.hero.title')}
        subtitle={t('landing:qualityPage.hero.subtitle')}
        primaryCta={{ label: t('landing:qualityPage.hero.ctaPrimary'), to: '/contact' }}
        secondaryCta={{ label: t('landing:qualityPage.hero.ctaSecondary'), to: '#modules' }}
        announcement={t('landing:qualityPage.hero.announcement')}
      />

      <DeckDisclaimer
        text={t('landing:complianceDisclaimer.text')}
        linkLabel={t('landing:complianceDisclaimer.learnMore')}
        linkTo="/terms#section10"
      />

      <DeckSection id="modules">
        <DeckSectionHeader
          badge={t('landing:qualityPage.modules.eyebrow')}
          title={t('landing:qualityPage.modules.title')}
        />
        <div className="grid lg:grid-cols-3 lg:divide-x lg:divide-white/10">
          {modules.map(({ key, icon: Icon, benefits }, idx) => (
            <div
              key={key}
              className={`py-10 animate-on-scroll ${idx === 0 ? 'lg:pr-10' : idx === modules.length - 1 ? 'lg:pl-10' : 'lg:px-10'}`}
            >
              <Icon className="w-6 h-6 text-sky-400 mb-5" />
              <h3 className="text-2xl font-light text-white mb-3">
                {t(`landing:qualityPage.modules.${key}.title`)}
              </h3>
              <p className="text-white/60 mb-8 leading-relaxed">
                {t(`landing:qualityPage.modules.${key}.description`)}
              </p>
              <ul className="space-y-3">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-white/70">
                    <span className="text-sky-400 mt-1">—</span>
                    <span>{t(`landing:qualityPage.modules.${key}.benefits.${b}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DeckSection>

      <DeckSection>
        <DeckSectionHeader
          badge={t('landing:howItWorks.sectionBadge')}
          title={t('landing:howItWorks.sectionTitle')}
          subtitle={t('landing:howItWorks.sectionSubtitle')}
        />
        <DeckStepGrid
          steps={howItWorksSteps.map(({ key, icon }) => ({
            number: t(`landing:howItWorks.steps.${key}.number`),
            title: t(`landing:howItWorks.steps.${key}.title`),
            description: t(`landing:howItWorks.steps.${key}.description`),
            icon,
          }))}
        />
      </DeckSection>

      <DeckSection>
        <DeckSectionHeader
          badge={t('landing:qualityPage.complianceBridge.eyebrow')}
          title={t('landing:qualityPage.complianceBridge.title')}
        />
        <div className="max-w-3xl animate-on-scroll">
          <p className="text-lg text-white/60 leading-relaxed mb-8">
            {t('landing:qualityPage.complianceBridge.description')}
          </p>
          <Link
            to="/compliance"
            className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 text-base font-medium transition-colors group"
          >
            {t('landing:qualityPage.complianceBridge.cta')}
            <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </DeckSection>

      <DeckSection>
        <DeckSectionHeader
          badge={t('landing:qualityPage.faq.eyebrow')}
          title={t('landing:qualityPage.faq.title')}
        />
        <DeckFaqList items={faqItems} />
      </DeckSection>

      <DeckFinalCta
        title={t('landing:qualityPage.finalCta.title')}
        subtitle={t('landing:qualityPage.finalCta.subtitle')}
        email="ventas@callcov.com"
        cta={{ label: t('landing:qualityPage.hero.ctaPrimary'), to: '/contact' }}
      />
    </PublicLayout>
  );
};

export default QualityLandingPage;
